import {
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
  Switch,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { IResultLayer } from '../../core/interfaces/result.interface';
import { ChartPattern } from '../../core/enums/chart-pattern.enum';
import { MuiColorInput } from 'mui-color-input';
import { sortLayerClasses } from '../../Utils/layer';

export interface ChartTabConfigurationProps {
  layer: IResultLayer;
  onLayerEdit: (index: number, layer: IResultLayer) => void;
}

export const ChartTabConfiguration = ({ layer, onLayerEdit }) => {
  const [layerData, setLayerData] = useState<IResultLayer>(layer);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const [timeoutToken, setTimeoutToken] = useState<NodeJS.Timeout>();

  const [displayName, setDisplayName] = useState<string>(layerData?.tab?.displayName || '');

  useEffect(() => {
    setLayerData(layer);
    setDisplayName(layer?.tab?.displayName || '');
    setSelectedTab(0);
  }, [layer?.resultId]);

  useEffect(() => {
    if (timeoutToken) clearTimeout(timeoutToken);

    const token = setTimeout(() => {
      const sortedLayerData = sortLayerClasses(layerData);
      onLayerEdit?.(sortedLayerData);
      setTimeoutToken(undefined);
    }, 300);

    setTimeoutToken(token);
  }, [layerData]);

  const sortedPieChart = [...layerData.tab.pieChart].sort((a, b) => {
    const classA = layerData.details.classes.find((c) => c.category === a.className);
    const classB = layerData.details.classes.find((c) => c.category === b.className);

    const valueA = classA?.infoValues?.[0] || 0;
    const valueB = classB?.infoValues?.[0] || 0;

    // Sort in descending order (largest first)
    return Number(valueB) - Number(valueA);
  });

  const updateOriginalPieChartItem = (
    className: string,
    updates: Partial<(typeof layerData.tab.pieChart)[0]>,
  ) => {
    const originalIndex = layerData.tab.pieChart.findIndex((item) => item.className === className);
    if (originalIndex !== -1) {
      Object.assign(layerData.tab.pieChart[originalIndex], updates);
      setLayerData({ ...layerData });
    }
  };

  return (
    <Box display="flex" flexDirection="column" sx={{ width: '100%' }}>
      <Typography variant="body2">Chart tab configuration</Typography>
      <TextField
        label="Tab Display Name"
        value={displayName}
        fullWidth
        margin="normal"
        onChange={(e) => {
          setDisplayName(e.target.value);
        }}
        onBlur={() => {
          layerData.tab.displayName = displayName;
          setLayerData({ ...layerData });
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <label htmlFor="chart-visibility">Chart Visibility</label>
        <Switch
          id="chart-visibility"
          checked={layerData.tab.visible}
          onChange={(e) => {
            layerData.tab.visible = e.target.checked;
            setLayerData({ ...layerData });
            const sortedLayerData = sortLayerClasses(layerData);
            onLayerEdit?.(sortedLayerData);
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', width: '100%', height: '420px', mt: 2 }}>
        <Tabs
          value={selectedTab}
          orientation="vertical"
          onChange={(_, idx) => setSelectedTab(idx)}
          aria-label="layers tabs"
          variant="scrollable"
          sx={{ borderRight: 1, borderColor: 'divider' }}
        >
          {sortedPieChart.map((chartData, index) => (
            <Tab
              key={`${chartData.className}_${index + 1}`}
              label={`${chartData.displayName || chartData.className}`}
              sx={{ fontSize: '10px' }}
            />
          ))}
        </Tabs>

        {sortedPieChart.map((c, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            display={i === selectedTab ? 'flex' : 'none'}
            justifyContent="space-evenly"
            alignContent="flex-start"
            flexDirection="column"
            sx={{ ml: 2 }}
          >
            <TextField
              label={`Category ${i + 1} Display Name`}
              defaultValue={c.displayName || c.className}
              value={c.displayName}
              fullWidth
              onChange={(e) => {
                updateOriginalPieChartItem(c.className, { displayName: e.target.value });
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  updateOriginalPieChartItem(c.className, { displayName: c.className });
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="Percent"
              value={c.percent}
              fullWidth
              onChange={(e) => {
                updateOriginalPieChartItem(c.className, { percent: +e.target.value });
              }}
            />
            <MuiColorInput
              label="Fill Color"
              value={c.fillColor}
              fullWidth
              isAlphaHidden={true}
              format="hex"
              onChange={(value) => {
                updateOriginalPieChartItem(c.className, { fillColor: value });
              }}
            />
            <Box sx={{ mt: 2, mb: 1 }}>
              <Typography gutterBottom sx={{ fontSize: '12px' }}>
                Fill Transparency ({c.fillTransparency})
              </Typography>
              <Slider
                value={c.fillTransparency}
                onChange={(e, value) => {
                  updateOriginalPieChartItem(c.className, { fillTransparency: +value });
                }}
                step={0.1}
                min={0}
                max={1}
                valueLabelDisplay="auto"
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel id={`fill-pattern-select-label-${i}`}>Fill Pattern</InputLabel>
              <Select
                labelId={`fill-pattern-select-label-${i}`}
                id={`fill-pattern-select-${i}`}
                value={c.fillPattern}
                label="Fill Pattern"
                onChange={(e) => {
                  updateOriginalPieChartItem(c.className, { fillPattern: e.target.value as ChartPattern });
                }}
              >
                {Object.keys(ChartPattern).map((key) => (
                  <MenuItem key={key} value={ChartPattern[key]}>
                    {ChartPattern[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
